export const RISK_COINS = ["BTC", "ETH", "SOL", "HYPE"];

const finite = (value) => typeof value === "number" && Number.isFinite(value);
const VALID_REGIMES = ["LONG_TERM_BULLISH", "LONG_TERM_BEARISH", "MIXED", "INSUFFICIENT_DATA"];
const VALID_LABELS = ["LONG_TERM_BULLISH", "LONG_TERM_BEARISH", "MIXED_REGIME", "ACCUMULATION_ZONE", "WAIT_FOR_CONFIRMATION", "AVOID_CHASING", "OVEREXTENDED", "HIGH_RISK", "NO_TRADE", "INSUFFICIENT_DATA"];
const VALID_BREAKOUTS = ["CONFIRMED_BREAKOUT", "UNCONFIRMED_BREAKOUT", "CONFIRMED_BREAKDOWN", "UNCONFIRMED_BREAKDOWN", "RANGE", "INSUFFICIENT_DATA"];
const VALID_ALIGNMENTS = ["BULLISH", "BEARISH", "MIXED", "INSUFFICIENT_DATA"];
const VALID_CONTEXT = ["BULLISH", "BEARISH", "NEUTRAL"];

export const normalizeRiskSnapshot = (analysis) => analysis?.technical_snapshot || null;

const isCompleteSnapshot = (snapshot) => Boolean(
  snapshot
  && VALID_REGIMES.includes(snapshot.market_regime)
  && VALID_LABELS.includes(snapshot.conservative_label)
  && VALID_BREAKOUTS.includes(snapshot.breakout_status)
  && VALID_ALIGNMENTS.includes(snapshot.multitimeframe_alignment)
  && finite(snapshot.atr_pct)
  && finite(snapshot.maximum_drawdown_30d_pct)
  && ![snapshot.market_regime, snapshot.conservative_label, snapshot.breakout_status, snapshot.multitimeframe_alignment].includes("INSUFFICIENT_DATA"),
);

/**
 * Aggregate risk is deterministic and language-independent: HIGH_RISK/NO_TRADE +3,
 * OVEREXTENDED +1, bearish regime +2, breakdown +2, ATR >=6% +1,
 * annualized volatility >=80% +1, and mixed alignment +1 per coin.
 */
export const aggregateMarketRisk = (analyses) => {
  const snapshots = RISK_COINS.map((coin) => normalizeRiskSnapshot(analyses?.[coin]));
  if (snapshots.length !== RISK_COINS.length || snapshots.some((snapshot) => !isCompleteSnapshot(snapshot))) return "INSUFFICIENT_DATA";
  const score = snapshots.reduce((total, snapshot) => total
    + (["HIGH_RISK", "NO_TRADE"].includes(snapshot.conservative_label) ? 3 : 0)
    + (snapshot.overextension_status === "OVEREXTENDED" ? 1 : 0)
    + (snapshot.market_regime === "LONG_TERM_BEARISH" ? 2 : 0)
    + (["CONFIRMED_BREAKDOWN", "UNCONFIRMED_BREAKDOWN"].includes(snapshot.breakout_status) ? 2 : 0)
    + (snapshot.atr_pct >= 6 ? 1 : 0)
    + (finite(snapshot.realized_volatility_30d_annualized_pct) && snapshot.realized_volatility_30d_annualized_pct >= 80 ? 1 : 0)
    + (snapshot.multitimeframe_alignment === "MIXED" ? 1 : 0), 0);
  const average = score / snapshots.length;
  if (average >= 5) return "HIGH";
  if (average >= 3) return "ELEVATED";
  if (average >= 1.5) return "MODERATE";
  return "LOW";
};

const marketStatus = (marketContext) => {
  const status = marketContext?.market?.status || marketContext?.sentiment;
  return VALID_CONTEXT.includes(status) ? status : null;
};

const validAnalysis = (analysis) => {
  const snapshot = normalizeRiskSnapshot(analysis);
  return Boolean(isCompleteSnapshot(snapshot)
    && ["ENTER", "WAIT", "AVOID", "SELL"].includes(analysis?.signal)
    && (analysis?.confidence === undefined || (Number.isInteger(analysis.confidence) && analysis.confidence >= 0 && analysis.confidence <= 100)));
};

const supportedEntry = (analysis, livePrice, snapshot) => (
  finite(livePrice)
  && finite(analysis?.entry_price)
  && finite(snapshot?.support)
  && livePrice >= snapshot.support * 0.98
  && livePrice <= snapshot.support * 1.02
);

const baseResult = (status) => ({ status, trader: status, investor: status, evidence: [], conditions: { constructive: [], cautious: [], invalidated: [] } });

/**
 * Precedence: insufficient data -> serious downside/high risk -> overextension ->
 * adverse/conflicting context -> confirmed constructive entry -> accumulation ->
 * hold -> wait. No prose is parsed here.
 */
export const classifyDecision = ({ analysis, marketContext, livePrice } = {}) => {
  const snapshot = normalizeRiskSnapshot(analysis);
  const context = marketStatus(marketContext);
  if (!validAnalysis(analysis) || !context) return { ...baseResult("INSUFFICIENT_DATA"), context };

  const serious = ["AVOID", "SELL"].includes(analysis.signal)
    || ["NO_TRADE", "HIGH_RISK"].includes(snapshot.conservative_label)
    || snapshot.breakout_status === "CONFIRMED_BREAKDOWN"
    || (snapshot.market_regime === "LONG_TERM_BEARISH" && snapshot.multitimeframe_alignment === "BEARISH");
  const evidence = [
    { key: "technical_signal", value: analysis.signal },
    { key: "long_term_trend", value: snapshot.market_regime },
    { key: "news_macro", value: context },
    { key: "risk_condition", value: snapshot.conservative_label },
  ];
  const conditions = { constructive: [], cautious: [], invalidated: [] };
  if (finite(snapshot.resistance)) conditions.constructive.push({ key: "breakout_above", value: snapshot.resistance });
  if (snapshot.volume_confirmed === false) conditions.constructive.push({ key: "volume_confirmation" });
  if (context === "BEARISH") conditions.cautious.push({ key: "market_context_adverse" });
  if (snapshot.low_volume_warning === true) conditions.cautious.push({ key: "low_volume" });
  if (snapshot.false_breakout_warning?.warning === true) conditions.cautious.push({ key: "false_breakout" });
  if (finite(analysis.stop_loss)) conditions.invalidated.push({ key: "stop_loss", value: analysis.stop_loss });
  if (snapshot.breakout_status.includes("BREAKDOWN")) conditions.invalidated.push({ key: "breakdown" });
  const common = { evidence, conditions, context, snapshot };
  if (serious) return { ...baseResult("AVOID_NEW_ENTRY"), ...common, trader: "AVOID_NEW_ENTRY", investor: "REDUCE_RISK" };
  if (["OVEREXTENDED", "AVOID_CHASING"].includes(snapshot.conservative_label) || snapshot.overextension_status === "OVEREXTENDED") {
    return { ...baseResult("WAIT_FOR_PULLBACK"), ...common, trader: "WAIT_FOR_PULLBACK", investor: "WAIT_FOR_PULLBACK" };
  }
  const confirmedEntry = analysis.signal === "ENTER"
    && (snapshot.breakout_status === "CONFIRMED_BREAKOUT" || supportedEntry(analysis, livePrice, snapshot))
    && snapshot.volume_confirmed === true
    && snapshot.multitimeframe_alignment === "BULLISH"
    && (!finite(snapshot.rsi14) || snapshot.rsi14 < 70)
    && context !== "BEARISH";
  if (confirmedEntry) return { ...baseResult("BUY_ON_CONFIRMATION"), ...common, trader: "BUY_ON_CONFIRMATION", investor: "ACCUMULATE_GRADUALLY" };
  const accumulate = snapshot.conservative_label === "ACCUMULATION_ZONE"
    && ["LONG_TERM_BULLISH", "MIXED"].includes(snapshot.market_regime)
    && snapshot.breakout_status !== "CONFIRMED_BREAKDOWN"
    && context !== "BEARISH";
  if (accumulate) return { ...baseResult("ACCUMULATE_GRADUALLY"), ...common, trader: "WAIT", investor: "ACCUMULATE_GRADUALLY" };
  if (context === "BEARISH" || snapshot.multitimeframe_alignment === "MIXED" || snapshot.breakout_status === "UNCONFIRMED_BREAKOUT" || snapshot.breakout_status === "UNCONFIRMED_BREAKDOWN") {
    return { ...baseResult("WAIT"), ...common, trader: "WAIT", investor: "WAIT" };
  }
  if (["LONG_TERM_BULLISH", "MIXED"].includes(snapshot.market_regime)) return { ...baseResult("HOLD"), ...common, trader: "WAIT", investor: "HOLD" };
  return { ...baseResult("WAIT"), ...common, trader: "WAIT", investor: "WAIT" };
};

export const marketEnvironment = (analyses, marketContext) => {
  const context = marketStatus(marketContext);
  const snapshots = RISK_COINS.map((coin) => normalizeRiskSnapshot(analyses?.[coin])).filter(isCompleteSnapshot);
  if (context === "BEARISH" && snapshots.filter((snapshot) => snapshot.market_regime === "LONG_TERM_BEARISH").length >= 2) return "BEARISH";
  if (context === "BULLISH" && snapshots.filter((snapshot) => snapshot.market_regime === "LONG_TERM_BULLISH").length >= 2) return "BULLISH";
  if (snapshots.length < 2 || !context) return "INSUFFICIENT_DATA";
  if (snapshots.every((snapshot) => snapshot.breakout_status === "RANGE")) return "RANGE";
  return "MIXED";
};
